import { CellValueChangedEvent } from 'ag-grid-community';
import { mapValues } from 'lodash';
import { useCallback, useMemo, useState } from 'react';

import { mapLocationForm } from 'contacts/components';
import { ContactGroupType } from 'contacts/enums';
import { useContactGroupActions } from 'contacts/hooks';
import { contactGroupService } from 'contacts/services';
import { IContactGroup, IContactGroupAddress } from 'contacts/types';
import { NotificationPosition, TableLayoutType } from 'core/enums';
import { AgGridServerTableProvider } from 'core/states';
import { AgGridGetRowsParams } from 'core/types';
import { removeNullValuesNestedObject } from 'core/utils';

import { locationColumnDef } from './columns';
import { LocationListTable } from './LocationListTable';

interface IProps {
  contactGroupType: ContactGroupType;
}

export const LocationList = ({ contactGroupType }: IProps) => {
  const [selectedContactGroup, setSelectedContactGroup] = useState<IContactGroup | null>(null);

  const { upsertContactGroupAddress } = useContactGroupActions(NotificationPosition.TOP);

  const openContactGroupDetails = useCallback(
    (data: IContactGroupAddress) => setSelectedContactGroup(data.contactGroup),
    []
  );

  const defaultColDef = useMemo(() => ({ autoHeight: true }), []);

  const columnDefs = useMemo(
    () => locationColumnDef({ contactGroupType, openContactGroupDetails }),
    [contactGroupType, openContactGroupDetails]
  );

  const mapFilters = useCallback((filters) => {
    let mappedFilters = filters;

    mappedFilters = mapValues(mappedFilters, (value, key) => {
      switch (key) {
        case 'isShipTo':
          if (value.includes('1') && !value.includes('0')) {
            return 1;
          }

          if (value.includes('0') && !value.includes('1')) {
            return 0;
          }

          return null;
        case 'isBillTo':
          if (value.includes('1') && !value.includes('0')) {
            return 1;
          }

          if (value.includes('0') && !value.includes('1')) {
            return 0;
          }

          return null;
        case 'spaEnabled':
          if (value.includes('true') && !value.includes('false')) {
            return 1;
          }

          if (value.includes('false') && !value.includes('true')) {
            return 0;
          }

          return null;
        default:
          return value;
      }
    });

    return removeNullValuesNestedObject(mappedFilters);
  }, []);

  const getRows = useCallback(
    ({ filters, ...rest }: AgGridGetRowsParams) => {
      return contactGroupService.getContactGroupAddresses({
        filters: {
          contactGroupType,
          ...mapFilters(filters),
        },
        ...rest,
      });
    },
    [contactGroupType, mapFilters]
  );

  const onCellValueChanged = useCallback(
    async (_, { colDef, data, newValue }: CellValueChangedEvent) => {
      const field = colDef.field;

      const { success, error, contactGroupAddress } = await upsertContactGroupAddress(
        mapLocationForm(
          {
            contactGroup: data.contactGroup,
            addressId: data.addressId,
            [field]: newValue,
          },
          contactGroupType
        )
      );

      if (!success) {
        throw new Error(error);
      }

      return { updatedRow: contactGroupAddress };
    },
    [contactGroupType, upsertContactGroupAddress]
  );

  const getLayoutType = useCallback(() => {
    switch (contactGroupType) {
      case ContactGroupType.VENDOR:
        return TableLayoutType.VENDOR_LOCATION;
      case ContactGroupType.CUSTOMER:
      default:
        return TableLayoutType.CUSTOMER_LOCATION;
    }
  }, [contactGroupType]);

  return (
    <AgGridServerTableProvider
      tableId="locationList"
      entityType="Location"
      defaultColDef={defaultColDef}
      columnDefs={columnDefs}
      getRows={getRows}
      onCellValueChanged={onCellValueChanged}
      layoutType={getLayoutType()}
      hasLayoutActions
      hasManageFilters
    >
      <LocationListTable
        selectedContactGroup={selectedContactGroup}
        setSelectedContactGroup={setSelectedContactGroup}
        contactGroupType={contactGroupType}
      />
    </AgGridServerTableProvider>
  );
};
